import {
  useCallback,
  useEffect,
  useState } from 'react';
import {
  Container,
  TextField,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  Box,
  Chip,
  CardActions
} from '@mui/material';
import Autocomplete from '../components/SearchableAutocomplete';
import { useTranslation } from 'react-i18next';
import {
  getAllClienti, creaCliente, updateClient,
  getAllCompanies, createCompany, updateCompany
} from '../APIs/ClientsAPI';
import SidebarLayout from '../components/sidebarLayout';
import { SetupWSEventListener } from '../utils/setupWSEventListener';
import { localizedErrorMessage } from '../utils/italianErrorMessage';


const EMPTY_CLIENTE = {
  name: '', surname: '', email: '', phone: '',
  address: '', streetNumber: '', zipCode: '', city: '', province: '',
  taxCode: '', vatNumber: '', reaNumber: '', reaOffice: '',
  roomNumber: '', treatment: '',
};
const EMPTY_COMPANY = {
  companyName: '', vatNumber: '', sdiCode: '',
  address: '', streetNumber: '', zipCode: '', city: '', province: '',
  taxCode: '', reaNumber: '', reaOffice: '',
  legalAddress: '', phone: '', email: '',
};


export default function ClientiPage() {
  const { t } = useTranslation();

  const [section, setSection] = useState('clienti');

  const [clienti, setClienti] = useState([]);
  const [query, setQuery] = useState('');
  const [cliente, setCliente] = useState(EMPTY_CLIENTE);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [editingClientId, setEditingClientId] = useState(null);
  const [clientFormError, setClientFormError] = useState('');
  const [clientFormSuccess, setClientFormSuccess] = useState('');

  const [companies, setCompanies] = useState([]);
  const [newCompany, setNewCompany] = useState(EMPTY_COMPANY);
  const [editingCompanyId, setEditingCompanyId] = useState(null);
  const [companyFormError, setCompanyFormError] = useState('');
  const [companyFormSuccess, setCompanyFormSuccess] = useState('');
  const [queryAziende, setQueryAziende] = useState('');

  const token = localStorage.getItem('accessToken');
  const tenant = localStorage.getItem('tenantId');

  const fetchClienti = useCallback(() => {
    getAllClienti(token, tenant)
      .then(data => setClienti(Array.isArray(data) ? data : []))
      .catch(err => console.error(err));
  }, [token, tenant]);

  const fetchCompanies = useCallback(() => {
    getAllCompanies(token, tenant)
      .then(data => setCompanies(Array.isArray(data) ? data : []))
      .catch(err => console.error(err));
  }, [token, tenant]);

  useEffect(() => {
    fetchClienti();
    fetchCompanies();
    const cleanup = SetupWSEventListener(fetchClienti);
    return cleanup;
  }, [fetchClienti, fetchCompanies]);

  const handleSearch = (e) => setQuery(e.target.value.toLowerCase());

  // ── Only name & surname are required. Email and phone are fully optional.
  const inserisciCliente = async () => {
    if (!cliente.name.trim() || !cliente.surname.trim()) {
      setClientFormError(t('client_page.error_required_fields'));
      return;
    }

    setClientFormError('');
    try {
      const payload = {
        name: cliente.name.trim(),
        surname: cliente.surname.trim(),
        ...(cliente.email.trim() && { email: cliente.email.trim() }),
        ...(cliente.phone.trim() && { phone: cliente.phone.trim() }),
        ...(cliente.address.trim() && { address: cliente.address.trim() }),
        ...(cliente.streetNumber.trim() && { streetNumber: cliente.streetNumber.trim() }),
        ...(cliente.zipCode.trim() && { zipCode: cliente.zipCode.trim() }),
        ...(cliente.city.trim() && { city: cliente.city.trim() }),
        ...(cliente.province.trim() && { province: cliente.province.trim() }),
        ...(cliente.taxCode.trim() && { taxCode: cliente.taxCode.trim() }),
        ...(cliente.vatNumber.trim() && { vatNumber: cliente.vatNumber.trim() }),
        ...(cliente.reaNumber.trim() && { reaNumber: cliente.reaNumber.trim() }),
        ...(cliente.reaOffice.trim() && { reaOffice: cliente.reaOffice.trim() }),
        ...(cliente.roomNumber.trim() && { roomNumber: cliente.roomNumber.trim() }),
        ...(cliente.treatment.trim() && { treatment: cliente.treatment.trim() }),
        ...(selectedCompany?.id ? { companyId: selectedCompany.id } : {}),
      };

      if (editingClientId) {
        await updateClient(editingClientId, payload, token, tenant);
      } else {
        await creaCliente(payload, token, tenant);
      }

      setCliente(EMPTY_CLIENTE);
      setSelectedCompany(null);
      setEditingClientId(null);
      setClientFormSuccess(t('client_page.success_create'));
      setTimeout(() => setClientFormSuccess(''), 3000);
      fetchClienti();
    } catch (err) {
      console.error(err);
      setClientFormError(localizedErrorMessage(err, t('client_page.error_create')));
    }
  };

  const inserisciAzienda = async () => {
    if (
      (!newCompany.companyName.trim()) ||
      (!newCompany.vatNumber.trim()) ||
      // (!newCompany.sdiCode.trim()) ||
      (!newCompany.address.trim()) ||
      // (!newCompany.legalAddress.trim()) ||
      (!newCompany.email.trim())
    ) {
      setCompanyFormError(t('client_page.error_company_required'));
      return;
    }
    setCompanyFormError('');
    try {
      if (editingCompanyId) {
        await updateCompany(editingCompanyId, newCompany, token, tenant);
      } else {
        await createCompany(newCompany, token, tenant);
      }
      setNewCompany(EMPTY_COMPANY);
      setEditingCompanyId(null);
      setCompanyFormSuccess(t('client_page.success_create_company_client'));
      setTimeout(() => setCompanyFormSuccess(''), 3000);
      fetchCompanies();
    } catch (err) {
      console.error(err);
      setCompanyFormError(localizedErrorMessage(err, t('client_page.error_create')));
    }
  };

  const handleEditCliente = (c) => {
    setSection('clienti');
    setEditingClientId(c.id);
    setClientFormError('');
    setClientFormSuccess('');
    setCliente({
      name: c.name || '',
      surname: c.surname || '',
      email: c.email || '',
      phone: c.phone || '',
      address: c.address || '',
      streetNumber: c.streetNumber || '',
      zipCode: c.zipCode || '',
      city: c.city || '',
      province: c.province || '',
      taxCode: c.taxCode || '',
      vatNumber: c.vatNumber || '',
      reaNumber: c.reaNumber || '',
      reaOffice: c.reaOffice || '',
      roomNumber: c.profile?.roomNumber ?? c.roomNumber ?? '',
      treatment: c.profile?.treatment ?? c.treatment ?? '',
    });
    setSelectedCompany(c.company || null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cancelEditCliente = () => {
    setEditingClientId(null);
    setCliente(EMPTY_CLIENTE);
    setSelectedCompany(null);
    setClientFormError('');
    setClientFormSuccess('');
  };

  const handleEditAzienda = (company) => {
    setSection('aziende');
    setEditingCompanyId(company.id);
    setCompanyFormError('');
    setCompanyFormSuccess('');
    setNewCompany({
      companyName: company.companyName || '',
      vatNumber: company.vatNumber || '',
      sdiCode: company.sdiCode || '',
      address: company.address || '',
      streetNumber: company.streetNumber || '',
      zipCode: company.zipCode || '',
      city: company.city || '',
      province: company.province || '',
      taxCode: company.taxCode || '',
      reaNumber: company.reaNumber || '',
      reaOffice: company.reaOffice || '',
      legalAddress: company.legalAddress || '',
      phone: company.phone || '',
      email: company.email || '',
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cancelEditAzienda = () => {
    setEditingCompanyId(null);
    setNewCompany(EMPTY_COMPANY);
    setCompanyFormError('');
    setCompanyFormSuccess('');
  };

  const opt = `(${t('optional')})`;

  const listaClienti = query.trim()
    ? clienti.filter(c =>
      c.name?.toLowerCase().includes(query) ||
      c.surname?.toLowerCase().includes(query) ||
      String(c.roomNumber ?? '').includes(query)
    )
    : clienti;

  const listaAziende = queryAziende.trim()
    ? companies.filter(c =>
      c.companyName?.toLowerCase().includes(queryAziende) ||
      c.vatNumber?.toLowerCase().includes(queryAziende) ||
      c.email?.toLowerCase().includes(queryAziende)
    )
    : companies;

  return (
    <SidebarLayout>
      <Container sx={{ mt: 4 }}>

        {/* ── SECTION TOGGLE */}
        <ToggleButtonGroup
          value={section}
          exclusive
          onChange={(_, val) => { if (val) setSection(val); }}
          sx={{ mb: 4 }}
        >
          <ToggleButton value="clienti">{t('client_page.btn_clienti')}</ToggleButton>
          <ToggleButton value="aziende">{t('client_page.btn_aziende')}</ToggleButton>
        </ToggleButtonGroup>

        {/* ════════════════════════════════════════
            CLIENTI SECTION
            ════════════════════════════════════════ */}
        {section === 'clienti' && (
          <>
            <Typography variant="h4" gutterBottom>
              {editingClientId ? t('update_button') : t('client_page.add_client')}
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={`${t('client_page.name')} *`}
                  value={cliente.name}
                  onChange={e => setCliente({ ...cliente, name: e.target.value })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={`${t('client_page.surname')} *`}
                  value={cliente.surname}
                  onChange={e => setCliente({ ...cliente, surname: e.target.value })}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={`${t('client_page.email')} ${opt}`}
                  value={cliente.email}
                  onChange={e => setCliente({ ...cliente, email: e.target.value })}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={`${t('client_page.phone')} ${opt}`}
                  value={cliente.phone}
                  onChange={e => setCliente({ ...cliente, phone: e.target.value })}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={`${t('client_page.address')} ${opt}`}
                  value={cliente.address}
                  onChange={e => setCliente({ ...cliente, address: e.target.value })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={`${t('client_page.street_number')} ${opt}`}
                  value={cliente.streetNumber}
                  onChange={e => setCliente({ ...cliente, streetNumber: e.target.value })}
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  fullWidth
                  label={`${t('client_page.zip_code')} ${opt}`}
                  value={cliente.zipCode}
                  onChange={e => setCliente({ ...cliente, zipCode: e.target.value })}
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  fullWidth
                  label={`${t('client_page.province')} ${opt}`}
                  value={cliente.province}
                  onChange={e => setCliente({ ...cliente, province: e.target.value })}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={`${t('client_page.city')} ${opt}`}
                  value={cliente.city}
                  onChange={e => setCliente({ ...cliente, city: e.target.value })}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={`${t('client_page.tax_code')} ${opt}`}
                  value={cliente.taxCode}
                  onChange={e => setCliente({ ...cliente, taxCode: e.target.value })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={`P.IVA / VAT ${opt}`}
                  value={cliente.vatNumber}
                  onChange={e => setCliente({ ...cliente, vatNumber: e.target.value })}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={`REA Number ${opt}`}
                  value={cliente.reaNumber}
                  onChange={e => setCliente({ ...cliente, reaNumber: e.target.value })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={`REA Office ${opt}`}
                  value={cliente.reaOffice}
                  onChange={e => setCliente({ ...cliente, reaOffice: e.target.value })}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={`${t('client_page.room')} ${opt}`}
                  value={cliente.roomNumber}
                  onChange={e => setCliente({ ...cliente, roomNumber: e.target.value })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label={`${t('client_page.treatment')} ${opt}`}
                  value={cliente.treatment}
                  onChange={e => setCliente({ ...cliente, treatment: e.target.value })}
                />
              </Grid>

              <Grid item xs={12}>
                <Autocomplete
                  options={companies}
                  getOptionLabel={o => `${o.companyName}${o.vatNumber ? ` (${o.vatNumber})` : ''}`}
                  value={selectedCompany}
                  onChange={(_, val) => setSelectedCompany(val)}
                  isOptionEqualToValue={(o, v) => o.id === v?.id}
                  noOptionsText={t('client_page.company_no_options')}
                  renderInput={params => (
                    <TextField
                      {...params}
                      label={`${t('client_page.company_name')} ${opt}`}
                      helperText={
                        companies.length === 0
                          ? t('client_page.company_hint')
                          : t('client_page.company_search_hint')
                      }
                    />
                  )}
                />
              </Grid>

              {clientFormError && <Grid item xs={12}><Alert severity="error">{clientFormError}</Alert></Grid>}
              {clientFormSuccess && <Grid item xs={12}><Alert severity="success">{clientFormSuccess}</Alert></Grid>}

              <Grid item xs={12}>
                <Button variant="contained" onClick={inserisciCliente}>
                  {editingClientId ? t('update_button') : t('client_page.add_client')}
                </Button>
                {editingClientId && (
                  <Button sx={{ ml: 1 }} onClick={cancelEditCliente}>
                    {t('cancel_button')}
                  </Button>
                )}
              </Grid>
            </Grid>

            <Divider sx={{ my: 4 }} />

            <Typography variant="h4" gutterBottom>{t('client_page.title')}</Typography>

            <TextField
              fullWidth
              label={t('client_page.search_label')}
              variant="outlined"
              value={query}
              onChange={handleSearch}
              sx={{ mb: 3 }}
            />

            {listaClienti.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {query.trim() ? t('client_page.no_results') : t('client_page.no_clienti')}
              </Typography>
            ) : (
              <Grid container spacing={2}>
                {listaClienti.map((c, i) => (
                  <Grid item xs={12} md={4} key={c.id ?? i}>
                    <Card sx={{ height: '100%' }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          {c.name} {c.surname}
                        </Typography>
                        {c.company && (
                          <Chip
                            label={`🏢 ${c.company.companyName}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                            sx={{ mb: 1 }}
                          />
                        )}
                        {c.email && <Typography variant="body2">📧 {c.email}</Typography>}
                        {c.phone && <Typography variant="body2">📞 {c.phone}</Typography>}
                        {c.address && <Typography variant="body2">📍 {c.address}</Typography>}
                        {c.zipCode && c.city && <Typography variant="body2">📍 {c.zipCode} {c.city}</Typography>}
                        {c.taxCode && <Typography variant="body2">📋 CF: {c.taxCode}</Typography>}
                        {c.vatNumber && <Typography variant="body2">🔢 P.IVA: {c.vatNumber}</Typography>}
                        {(c.profile?.roomNumber ?? c.roomNumber) && (
                          <Typography variant="body2">
                            🛏 {t('client_page.room')}: {c.profile?.roomNumber ?? c.roomNumber}
                          </Typography>
                        )}
                        {(c.profile?.treatment ?? c.treatment) && (
                          <Typography variant="body2">
                            💼 {t('client_page.treatment')}: {c.profile?.treatment ?? c.treatment}
                          </Typography>
                        )}
                      </CardContent>
                      <CardActions>
                        <Button size="small" onClick={() => handleEditCliente(c)}>
                          {t('update_button')}
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </>
        )}

        {/* ════════════════════════════════════════
            AZIENDE SECTION
            ════════════════════════════════════════ */}
        {section === 'aziende' && (
          <>
            <Typography variant="h4" gutterBottom>
              {editingCompanyId ? t('update_button') : t('client_page.add_company')}
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6}>
                <TextField fullWidth label={`${t('client_page.company_name')} *`}
                  value={newCompany.companyName}
                  onChange={e => setNewCompany({ ...newCompany, companyName: e.target.value })} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="P.IVA / VAT *"
                  value={newCompany.vatNumber}
                  onChange={e => setNewCompany({ ...newCompany, vatNumber: e.target.value })} />
              </Grid>

              <Grid item xs={6}>
                <TextField fullWidth label={`${t('client_page.tax_code')} ${opt}`}
                  value={newCompany.taxCode}
                  onChange={e => setNewCompany({ ...newCompany, taxCode: e.target.value })} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label={`SDI Code ${opt}`}
                  value={newCompany.sdiCode}
                  onChange={e => setNewCompany({ ...newCompany, sdiCode: e.target.value })} />
              </Grid>

              <Grid item xs={12}>
                <TextField fullWidth label={`${t('client_page.address')} ${opt}`}
                  value={newCompany.address}
                  onChange={e => setNewCompany({ ...newCompany, address: e.target.value })} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label={`${t('client_page.street_number')} ${opt}`}
                  value={newCompany.streetNumber}
                  onChange={e => setNewCompany({ ...newCompany, streetNumber: e.target.value })} />
              </Grid>
              <Grid item xs={3}>
                <TextField fullWidth label={`${t('client_page.zip_code')} ${opt}`}
                  value={newCompany.zipCode}
                  onChange={e => setNewCompany({ ...newCompany, zipCode: e.target.value })} />
              </Grid>
              <Grid item xs={3}>
                <TextField fullWidth label={`${t('client_page.province')} ${opt}`}
                  value={newCompany.province}
                  onChange={e => setNewCompany({ ...newCompany, province: e.target.value })} />
              </Grid>

              <Grid item xs={12}>
                <TextField fullWidth label={`${t('client_page.city')} ${opt}`}
                  value={newCompany.city}
                  onChange={e => setNewCompany({ ...newCompany, city: e.target.value })} />
              </Grid>

              <Grid item xs={12}>
                <TextField fullWidth label={`${t('client_page.legal_address')} ${opt}`}
                  value={newCompany.legalAddress}
                  onChange={e => setNewCompany({ ...newCompany, legalAddress: e.target.value })} />
              </Grid>

              <Grid item xs={6}>
                <TextField fullWidth label={`REA Number ${opt}`}
                  value={newCompany.reaNumber}
                  onChange={e => setNewCompany({ ...newCompany, reaNumber: e.target.value })} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label={`REA Office ${opt}`}
                  value={newCompany.reaOffice}
                  onChange={e => setNewCompany({ ...newCompany, reaOffice: e.target.value })} />
              </Grid>

              <Grid item xs={6}>
                <TextField fullWidth label={`${t('client_page.phone')} ${opt}`}
                  value={newCompany.phone}
                  onChange={e => setNewCompany({ ...newCompany, phone: e.target.value })} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label={`${t('client_page.company_email')} ${opt}`}
                  value={newCompany.email}
                  onChange={e => setNewCompany({ ...newCompany, email: e.target.value })} />
              </Grid>

              {companyFormError && <Grid item xs={12}><Alert severity="error">{companyFormError}</Alert></Grid>}
              {companyFormSuccess && <Grid item xs={12}><Alert severity="success">{companyFormSuccess}</Alert></Grid>}

              <Grid item xs={12}>
                <Button variant="contained" onClick={inserisciAzienda}>
                  {editingCompanyId ? t('update_button') : t('client_page.add_company')}
                </Button>
                {editingCompanyId && (
                  <Button sx={{ ml: 1 }} onClick={cancelEditAzienda}>
                    {t('cancel_button')}
                  </Button>
                )}
              </Grid>
            </Grid>

            <Divider sx={{ my: 4 }} />

            <Typography variant="h4" gutterBottom>{t('client_page.company_management')}</Typography>

            <TextField
              fullWidth
              label={t('client_page.search_companies_label')}
              variant="outlined"
              value={queryAziende}
              onChange={e => setQueryAziende(e.target.value.toLowerCase())}
              sx={{ mb: 3 }}
            />

            {listaAziende.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {queryAziende.trim() ? t('client_page.no_results') : t('client_page.no_companies')}
              </Typography>
            ) : (
              <Grid container spacing={2}>
                {listaAziende.map((company, i) => {
                  const linkedClients = clienti.filter(
                    c => c.companyId === company.id || c.company?.id === company.id
                  );
                  return (
                    <Grid item xs={12} md={4} key={company.id ?? i}>
                      <Card sx={{ height: '100%' }}>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            🏢 {company.companyName}
                          </Typography>
                          {company.vatNumber && <Typography variant="body2">🔢 P.IVA: {company.vatNumber}</Typography>}
                          {company.taxCode && <Typography variant="body2">📋 Cod. Fiscale: {company.taxCode}</Typography>}
                          {company.sdiCode && <Typography variant="body2">📄 SDI: {company.sdiCode}</Typography>}
                          {company.reaNumber && <Typography variant="body2">🏛️ REA: {company.reaNumber}</Typography>}
                          {company.address && <Typography variant="body2">📍 {company.address}</Typography>}
                          {company.streetNumber && <Typography variant="body2">📍 {company.streetNumber}</Typography>}
                          {company.zipCode && company.city && <Typography variant="body2">📍 {company.zipCode} {company.city} ({company.province})</Typography>}
                          {company.legalAddress && <Typography variant="body2">⚖️ {company.legalAddress}</Typography>}
                          {company.email && <Typography variant="body2">📧 {company.email}</Typography>}
                          {company.phone && <Typography variant="body2">📞 {company.phone}</Typography>}
                          {linkedClients.length > 0 && (
                            <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                {t('client_page.linked_clients')}:
                              </Typography>
                              {linkedClients.map(c => (
                                <Chip
                                  key={c.id}
                                  label={`${c.name} ${c.surname}`}
                                  size="small"
                                  variant="outlined"
                                  sx={{ mr: 0.5, mb: 0.5 }}
                                />
                              ))}
                            </Box>
                          )}
                        </CardContent>
                        <CardActions>
                          <Button size="small" onClick={() => handleEditAzienda(company)}>
                            {t('update_button')}
                          </Button>
                        </CardActions>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </>
        )}

      </Container>
    </SidebarLayout>
  );
}
